<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <div class="flex-1 flex flex-col gap-5 container-padding pb-24 py-4">
      <!-- Backup Actions -->
      <div class="space-y-4">
      <!-- Export Backup Card -->
      <ModuleShell
        :title="$t('views.more.backup.export.title')"
        :icon="Download"
        hide-action
        compact>
        <p class="text-sm text-[var(--color-text-secondary)] mb-3">
          {{ $t('views.more.backup.export.description') }}
        </p>
        <button
          @click="handleExport"
          :disabled="loading"
          class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-600)]
                 text-white font-medium shadow
                 disabled:opacity-50 disabled:cursor-not-allowed
                 active:scale-[0.98] transition">
          <Download class="w-5 h-5" />
          <span>{{ $t('views.more.backup.export.button') }}</span>
        </button>
      </ModuleShell>

      <!-- Import Backup Card -->
      <ModuleShell
        :title="$t('views.more.backup.import.title')"
        :icon="Upload"
        hide-action
        compact>
        <p class="text-sm text-[var(--color-text-secondary)] mb-3">
          {{ $t('views.more.backup.import.description') }}
        </p>
        <button
          @click="handleImportClick"
          :disabled="loading"
          class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                 bg-amber-600 hover:bg-amber-700
                 text-white font-medium shadow
                 disabled:opacity-50 disabled:cursor-not-allowed
                 active:scale-[0.98] transition">
          <Upload class="w-5 h-5" />
          <span>{{ $t('views.more.backup.import.button') }}</span>
        </button>
      </ModuleShell>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <ConfirmAction
      v-if="showConfirmation"
      :message="$t('views.more.backup.import.confirmMessage')"
      :confirmMessage="$t('common.buttons.confirm')"
      variant="warning"
      @confirm="handleImportConfirm"
      @cancel="showConfirmation = false" />

    <!-- Toast Notifications -->
    <Toast
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :auto-hide="true"
      :duration="3000" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useUiStore } from '@/stores/ui.store.js'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import ConfirmAction from '@/components/actions/ConfirmAction.vue'
import Toast from '@/components/ui/feedback/Toast.vue'
import { Download, Upload } from 'lucide-vue-next'
import { backupService } from '@/services/backup/backup.service.js'
import { backupFileHandler } from '@/services/backup/backup-file-handler.js'

const { t } = useI18n()
const router = useRouter()
const uiStore = useUiStore()

onMounted(() => {
  uiStore.setHeader({
    title: t('views.more.backup.title'),
    showBack: true,
    showAdd: false
  })
})

// State
const loading = ref(false)
const showConfirmation = ref(false)
const pendingBackupData = ref(null)
const toast = ref({
  show: false,
  message: '',
  type: 'info'
})

/**
 * Handle export backup
 */
async function handleExport() {
  if (loading.value) return
  
  loading.value = true
  
  try {
    // Export database
    const backupData = await backupService.exportBackup()
    
    // Generate filename
    const filename = backupService.generateBackupFilename()
    
    // Download file
    await backupFileHandler.downloadBackup(backupData, filename)
    
    // Show success toast
    showToast(t('views.more.backup.export.success'), 'saved')
  } catch (error) {
    console.error('Export failed:', error)
    showToast(
      t('views.more.backup.export.error', { error: error.message }),
      'error'
    )
  } finally {
    loading.value = false
  }
}

/**
 * Handle import button click (show file picker)
 */
async function handleImportClick() {
  if (loading.value) return
  
  loading.value = true
  
  try {
    // Show file picker and read backup
    const backupData = await backupFileHandler.uploadBackup()
    
    // Store backup data for confirmation
    pendingBackupData.value = backupData
    
    // Show confirmation dialog
    showConfirmation.value = true
  } catch (error) {
    console.error('File upload failed:', error)
    
    // Only show error if it's not a cancellation
    if (error.message !== 'File selection cancelled') {
      showToast(
        t('views.more.backup.import.uploadError', { error: error.message }),
        'error'
      )
    }
  } finally {
    loading.value = false
  }
}

/**
 * Handle import confirmation (after user confirms wipe)
 */
async function handleImportConfirm() {
  showConfirmation.value = false
  loading.value = true
  
  try {
    // Import backup (wipe + restore)
    await backupService.importBackup(pendingBackupData.value)
    
    // Show success toast
    showToast(t('views.more.backup.import.success'), 'saved')
    
    // Wait a moment then reload to refresh all stores
    setTimeout(() => {
      window.location.reload()
    }, 1500)
  } catch (error) {
    console.error('Import failed:', error)
    showToast(
      t('views.more.backup.import.error', { error: error.message }),
      'error'
    )
  } finally {
    loading.value = false
    pendingBackupData.value = null
  }
}

/**
 * Show toast notification
 */
function showToast(message, type = 'info') {
  toast.value = {
    show: true,
    message,
    type
  }
  
  // Reset after duration
  setTimeout(() => {
    toast.value.show = false
  }, 3100)
}
</script>
